// Supabase Storage Client
// Handles file uploads to Supabase Storage (replacing R2)

import { supabase } from './supabase';
import * as Sentry from '@sentry/react';

interface UploadResult {
  fileKey: string;
  publicUrl: string;
}

const BUCKET_NAME = 'documents';

/**
 * Upload a file to Supabase Storage
 * Files are stored in user-specific folders: {userId}/{uuid}-{filename}
 */
export async function uploadDocument(
  file: File,
  userId: string,
  onProgress?: (percent: number) => void
): Promise<UploadResult> {
  const fileKey = `${userId}/${crypto.randomUUID()}-${file.name}`;

  try {
    // Signal upload started
    onProgress?.(5);

    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(fileKey, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'application/octet-stream',
      });

    if (error) {
      throw error;
    }

    onProgress?.(90);

    // Get a signed URL for authenticated access (bucket is private)
    const { data: urlData, error: urlError } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(data.path, 3600);

    onProgress?.(100);

    return {
      fileKey: data.path,
      publicUrl: urlError ? '' : urlData.signedUrl,
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'storage-upload' },
      extra: { fileName: file.name, fileType: file.type, fileSize: file.size, userId },
    });
    throw error;
  }
}

/**
 * Upload a file with chunked upload for large files (> 6MB)
 * Uses resumable uploads for better reliability
 */
export async function uploadDocumentResumable(
  file: File,
  userId: string,
  onProgress?: (percent: number) => void
): Promise<UploadResult> {
  const fileKey = `${userId}/${crypto.randomUUID()}-${file.name}`;

  try {
    onProgress?.(5);

    // Use createSignedUploadUrl for larger files
    const { data: signedData, error: signedError } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUploadUrl(fileKey);

    if (signedError) {
      throw signedError;
    }

    // Upload using the signed URL with XMLHttpRequest for progress tracking
    await uploadWithProgress(signedData.signedUrl, signedData.token, file, onProgress);

    // Get a signed URL for authenticated access (bucket is private)
    const { data: urlData, error: urlError } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(fileKey, 3600);

    return {
      fileKey,
      publicUrl: urlError ? '' : urlData.signedUrl,
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'storage-upload-resumable' },
      extra: { fileName: file.name, fileType: file.type, fileSize: file.size, userId },
    });
    throw error;
  }
}

/**
 * Upload file with progress tracking using XMLHttpRequest
 */
function uploadWithProgress(
  url: string,
  token: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        // Scale progress from 5-95% (leaving room for init and finalization)
        const percent = Math.round(5 + (event.loaded / event.total) * 90);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: Network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

/**
 * Get a signed URL for a file stored in Supabase Storage (bucket is private)
 */
export async function getDocumentUrl(fileKey: string, expiresIn = 3600): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(fileKey, expiresIn);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}

/**
 * Delete a file from storage
 */
export async function deleteDocument(fileKey: string): Promise<void> {
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([fileKey]);

  if (error) {
    Sentry.captureException(error, {
      tags: { component: 'storage-delete' },
      extra: { fileKey },
    });
    throw error;
  }
}

/**
 * Get a signed URL for temporary private access
 * Useful for sensitive documents that shouldn't be publicly accessible
 */
export async function getSignedUrl(fileKey: string, expiresIn: number = 3600): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .createSignedUrl(fileKey, expiresIn);

  if (error) {
    throw error;
  }

  return data.signedUrl;
}

/**
 * Check if a file exists in storage
 */
export async function fileExists(fileKey: string): Promise<boolean> {
  const { data, error } = await supabase.storage
    .from(BUCKET_NAME)
    .list(fileKey.split('/').slice(0, -1).join('/'), {
      search: fileKey.split('/').pop(),
    });

  if (error) {
    return false;
  }

  return data.length > 0;
}
